import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export interface Trip {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  visitCount: number;
  isActive: boolean;
  isUpcoming: boolean;
  daysUntilStart: number;
  isOwner: boolean;
  collaboratorRole: string | null;
}

export function useTrips() {
  return useQuery({
    queryKey: ['trips'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/trips`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<Trip[]>;
    },
  });
}

export function useTrip(id: string) {
  return useQuery({
    queryKey: ['trip', id],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/trips/${id}`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<Trip>;
    },
    enabled: !!id,
  });
}

export function useCreateTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; startDate: string; endDate: string }) => {
      const response = await fetch(`${API_BASE}/api/trips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create trip');
      }
      return response.json() as Promise<Trip>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['my-trips'] });
    },
  });
}

export function useDeleteTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tripId: string) => {
      const response = await fetch(`${API_BASE}/api/trips/${tripId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete trip');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['my-trips'] });
    },
  });
}

export function useUpdateTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; startDate?: string; endDate?: string } }) => {
      const response = await fetch(`${API_BASE}/api/trips/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update trip');
      }
      return response.json() as Promise<Trip>;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['my-trips'] });
      queryClient.invalidateQueries({ queryKey: ['trip', id] });
    },
  });
}

export function useCloneTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, data }: { tripId: string; data: { name: string; startDate: string; endDate: string } }) => {
      const response = await fetch(`${API_BASE}/api/trips/${tripId}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to clone trip');
      }
      return response.json() as Promise<Trip>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['my-trips'] });
    },
  });
}
